import React from 'react'
import { filterNavigationEntries, NavigationItem, NavigationItemFilterCheck } from '../sidebarHelper'
import { ContextRoute } from '../../app/context'
import { NavigationListView } from './NavigationListView'
import { NavigationContext } from '../SideBarNavigation'

interface Props {
    route: ContextRoute
    showTooltip: boolean
    navigationContext: NavigationContext
    currentOrganization: string | undefined
    filterProps: NavigationItemFilterCheck
}

export class ProjectNavigationItems extends React.PureComponent<Props> {
    private entries: NavigationItem[] = [
        {
            label: 'Performance',
            icon: 'fa-area-chart',
            route: 'XhprofOrganizationBundle.Application.show',
            supportsAllServices: false,
            filterCheck: props => !props.isProfilingSpace,
            alternativeRoutes: {
                'XhprofProfilerBundle.Transaction.show': true,
                'XhprofProfilerBundle.OperationSnapshot.show': true,
                'XhprofProfilerBundle.Snapshot.show': true,
            },
        },
        {
            label: 'Traces',
            icon: 'fa-sitemap',
            route: 'XhprofProfilerBundle.Profile.list',
            parameters: { hours: 24 },
            alternativeRoutes: {
                'XhprofProfilerBundle.Profile.listTracesHistory': true,
                'XhprofProfilerBundle.Profile.quick': true,
                'XhprofProfilerBundle.Profile.flamegraph': true,
                'XhprofProfilerBundle.Profile.summary': true,
                'XhprofProfilerBundle.Profile.callgraph': true,
                'XhprofProfilerBundle.Profile.bottlenecksReport': true,
                'XhprofProfilerBundle.Profile.logs': true,
                'XhprofProfilerBundle.Profile.compare': true,
                'XhprofProfilerBundle.Profile.flamegraphCompare': true,
                'XhprofProfilerBundle.Profile.listCompare': true,
                'XhprofProfilerBundle.Profile.callgraphCompare': true,
                'XhprofOrganizationBundle.Application.trigger': true,
            },
        },
        {
            label: 'Tracepoints',
            icon: 'fa-crosshairs',
            route: 'XhprofProfilerBundle.Profile.listTracepoints',
            filterCheck: props => !props.isProfilingSpace,
            alternativeRoutes: {
                'XhprofProfilerBundle.Profile.tracepoint': true,
            },
        },
        {
            label: 'History',
            icon: 'fa-history',
            route: 'XhprofProfilerBundle.History.lastWeek',
            filterCheck: props => !props.isProfilingSpace,
            alternativeRoutes: {
                'XhprofProfilerBundle.History.history': true,
                'XhprofProfilerBundle.History.operationHistory': true,
            },
        },
        {
            icon: 'fa-bug',
            label: 'Errors / Exceptions',
            route: 'XhprofErrorBundle.Issues.errors',
            filterCheck: props => !props.isProfilingSpace,
        },
        {
            icon: 'fa-exclamation-triangle',
            label: 'Non-Fatal Errors',
            route: 'XhprofErrorBundle.Issues.nonFatals',
            filterCheck: props => props.license?.deprecationTracking && props.license?.warningsAndNotices && !props.isProfilingSpace,
        },
        {
            icon: 'fa-exclamation-triangle',
            label: 'Deprecations',
            route: 'XhprofErrorBundle.Issues.deprecations',
            filterCheck: props => props.license?.deprecationTracking && !props.license?.warningsAndNotices && !props.isProfilingSpace,
        },
        {
            icon: 'fa-database',
            label: 'Slow SQLs',
            route: 'XhprofErrorBundle.Issues.slowSql',
            filterCheck: props => !props.isProfilingSpace,
        },
        {
            icon: 'fa-rocket',
            label: 'Releases',
            route: 'XhprofErrorBundle.Issues.release',
            filterCheck: props => props.license?.events && !props.isProfilingSpace,
        },
        {
            icon: 'fa-warning',
            label: 'Incidents',
            route: 'XhprofErrorBundle.Issues.incident',
            filterCheck: props => props.license?.notifications && !props.isProfilingSpace,
        },
        {
            icon: 'fa-lightbulb-o',
            label: 'Observations',
            route: 'XhprofErrorBundle.Issues.observation',
            filterCheck: props => props.license?.observations && !props.isProfilingSpace,
        },
        {
            label: 'Project Settings',
            icon: 'fa-cog',
            route: 'XhprofOrganizationBundle.Application.settings',
            alwaysSkipHistoryApi: true,
            alternativeRoutes: {
                'XhprofOrganizationBundle.Application.settings': true,
                'XhprofErrorBundle.Error.listExceptionTracking': true,
                'XhprofProfilerBundle.Environment.list': true,
                'XhprofProfilerBundle.Server.list': true,
                'XhprofProfilerBundle.Service.list': true,
                'XhprofProfilerBundle.Transaction.list': true,
                'XhprofAlertBundle.Configuration.list': true,
                'XhprofOrganizationBundle.Application.installation': true,
                'XhprofOrganizationBundle.Application.license': true,
                'XhprofOrganizationBundle.Application.changeTimeZone': true,
                'XhprofOrganizationBundle.Application.rootPathMappings': true,
                'XhprofAlertBundle.Configuration.preview': true,
                'XhprofAlertBundle.Configuration.editCheck': true,
                'XhprofAlertBundle.Configuration.confirmDeleteCheck': true,
                'XhprofProfilerBundle.Transaction.confirmVisibilityChanges': true,
                'XhprofProfilerBundle.Transaction.ignore': true,
                'XhprofProfilerBundle.Transaction.activate': true,
                'XhprofProfilerBundle.Transaction.deactivate': true,
                'XhprofProfilerBundle.Transaction.remove': true,
                'XhprofProfilerBundle.Transaction.removeAll': true,
                'XhprofProfilerBundle.Transaction.settings.edit': true,
                'XhprofProfilerBundle.Transaction.viewTransactionCheck': true,
                'XhprofErrorBundle.IgnoredExceptions.list': true,
                'XhprofProfilerBundle.Environment.create': true,
                'XhprofProfilerBundle.Server.confirmDelete': true,
                'XhprofProfilerBundle.Service.changeResponseTimeTarget': true,
                'XhprofProfilerBundle.Service.makeDefault': true,
                'XhprofProfilerBundle.Service.deactivate': true,
                'XhprofOrganizationBundle.Application.rename': true,
                'XhprofOrganizationBundle.Application.remove': true,
            },
        },
    ]

    public render() {
        const { filterProps, route, showTooltip, navigationContext, currentOrganization } = this.props

        const filteredEntries = filterNavigationEntries(this.entries, filterProps)

        return (
            <NavigationListView
                items={filteredEntries}
                showTooltip={showTooltip}
                shouldSkipHistoryApi={navigationContext === 'project-settings'}
                currentRoute={route.route}
                currentOrganization={currentOrganization}
            />
        )
    }
}
